import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem, Grid, Chip,
  CircularProgress, Alert, Snackbar, IconButton, Autocomplete, ToggleButtonGroup, ToggleButton, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, getDocs, addDoc, updateDoc, serverTimestamp, query, orderBy, where, runTransaction, doc, getDoc } from 'firebase/firestore';
import PaymentDialog from '../components/PaymentDialog';
import NewPatientDialog from '../components/NewPatientDialog';
import PatientSearch from '../components/PatientSearch';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { crearPresupuestoAutomatico } from '../services/presupuestoAutoService';
import { registrarVentaOrden } from '../services/ventaService';
import { printOrderPDF } from './ImagingOrderDetail';

import { COLORS } from '../theme/constants';
import { PRIORITY_CONFIG, MODALITY_CONFIG, generateOrderNumber, calcAge } from '../models/imagingModel';

const { primary: IMG_PRIMARY, teal: IMG_TEAL } = COLORS;

const getNextOrderNumber = async (retries = 3) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const counterRef = doc(db, 'counters', 'imagingOrders');
  
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const newCount = await runTransaction(db, async (tx) => {
        const snap = await tx.get(counterRef);
        const current = snap.exists() ? snap.data().lastNumber : 999;
        const next = current + 1;
        tx.set(counterRef, { lastNumber: next }, { merge: true });
        return next;
      });
      return `IMG-${y}${m}${d}-${newCount}`;
    } catch (err) {
      if (err.code === 'resource-exhausted' || err.code === 'aborted') {
        const delay = Math.pow(2, attempt) * 500;
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      throw err;
    }
  }
  throw new Error('No se pudo generar el número de orden después de varios intentos');
};

export default function NewImagingOrder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPatientId = searchParams.get('patientId');
  const homeUrl = urlPatientId ? `/patients/${urlPatientId}/record` : '/imaging/orders';
  const { currentUser, userData } = useAuth();
  const { activeBranchId, activeBranch, filterByBranch } = useBranch();
  const isGestion = (userData?.role || '').toLowerCase() === 'gestion';

  const [professionals, setProfessionals] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [aranceles, setAranceles] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingPatient, setLoadingPatient] = useState(false);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedVet, setSelectedVet] = useState(null);
  const [selectedStudies, setSelectedStudies] = useState([]);
  const [priority, setPriority] = useState('routine');
  const [clinicalIndication, setClinicalIndication] = useState('');
  const [relevantHistory, setRelevantHistory] = useState('');
  const [provisionalDiagnosis, setProvisionalDiagnosis] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [newPatientOpen, setNewPatientOpen] = useState(false);

  useEffect(() => {
    const loadAll = async () => {
      const [proSnap, etSnap, diagSnap, aSnap] = await Promise.all([
        getDocs(query(collection(db, 'professionals'), where('active', '!=', false))),
        getDocs(query(collection(db, 'imagingExamTypes'), where('isActive', '==', true))),
        getDocs(collection(db, 'diagnoses')),
        getDocs(query(collection(db, 'aranceles'))),
      ]);

      setProfessionals(filterByBranch(proSnap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true }));
      setExamTypes(filterByBranch(etSnap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true }).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })));
      setDiagnoses(filterByBranch(diagSnap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true }));
      setAranceles(filterByBranch(aSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((a) => a.activo !== false), { includeUnassigned: true }));
      setLoadingData(false);
    };
    loadAll().catch(() => setLoadingData(false));
  }, []);

  // Auto-seleccionar paciente si viene de la URL
  useEffect(() => {
    if (urlPatientId && !selectedPatient) {
      setLoadingPatient(true);
      getDoc(doc(db, 'patients', urlPatientId))
        .then(snap => {
          if (snap.exists()) {
            setSelectedPatient({ id: snap.id, ...snap.data() });
          }
        })
        .finally(() => setLoadingPatient(false));
    }
  }, [urlPatientId, selectedPatient]);

  // Auto-seleccionar veterinario actual
  useEffect(() => {
    if (['médico veterinario', 'director médico'].includes(userData?.role?.toLowerCase()) && !selectedVet) {
      const currentVet = professionals.find(p => p.id === userData?.professionalId);
      if (currentVet) {
        setSelectedVet(currentVet);
      }
    }
  }, [professionals, userData, selectedVet]);

  const handleAddStudy = (examType) => {
    if (!examType) return;
    setSelectedStudies(prev => [...prev, {
      examTypeId: examType.id,
      examTypeName: examType.name,
      modality: examType.modality,
      bodyRegion: examType.defaultBodyRegion || '',
      views: examType.defaultViews || [],
      side: null,
      sedationRequired: examType.sedationTypical || false,
      contrastRequired: examType.contrastTypical || false,
      contrastType: null,
      precioNeto: examType.precioNeto || 0,
    }]);
  };

  const handleUpdateStudy = (index, field, value) => {
    setSelectedStudies(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleRemoveStudy = (index) => {
    setSelectedStudies(prev => prev.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    const subtotal = selectedStudies.reduce((sum, s) => sum + (s.precioNeto || 0), 0);
    const iva = Math.round(subtotal * 0.19);
    const total = subtotal + iva;
    return { subtotal, iva, total };
  };

  const displayedCategories = useMemo(() => {
    const sorted = [...examTypes].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
    );
    const byCat = sorted.reduce((acc, e) => {
      const cat = e.category || 'Otros';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(e);
      return acc;
    }, {});
    const out = [];
    Object.keys(byCat)
      .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
      .forEach((cat) => out.push({ category: cat, exams: byCat[cat] }));
    return out;
  }, [examTypes]);

  const handleToggleStudy = (exam) => {
    const exists = selectedStudies.some((s) => s.examTypeId === exam.id);
    if (exists) {
      setSelectedStudies((prev) => prev.filter((s) => s.examTypeId !== exam.id));
    } else {
      handleAddStudy(exam);
    }
  };

  const normalizeStr = (s) =>
    (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

  const crearPresupuestoDesdeOrden = async (orderNumber, orderId, orderType, studies) => {
    if (!selectedPatient || studies.length === 0) return null;
    return crearPresupuestoAutomatico({
      orderId,
      orderType,
      orderNumber,
      patientId: selectedPatient.id,
      patientName: selectedPatient.name,
      patientSpecies: selectedPatient.species || '',
      ownerId: selectedPatient.ownerId || '',
      ownerName: selectedPatient.ownerName || '',
      ownerPhone: selectedPatient.ownerPhone || '',
      branchId: activeBranchId || userData?.branchId || '',
      vetId: selectedVet?.id || currentUser?.uid || '',
      vetName: selectedVet ? `Dr/a. ${selectedVet.nombre} ${selectedVet.apellidos || ''}`.trim() : (userData?.name || currentUser?.email || ''),
      items: studies.map((s) => {
        const nameNorm = normalizeStr(s.examTypeName);
        const arancel = aranceles.find((a) => normalizeStr(a.prestacion) === nameNorm) ||
                        aranceles.find((a) => normalizeStr(a.prestacion).includes(nameNorm));
        return {
          arancel,
          nombre: s.examTypeName,
          cantidad: 1,
          precioFallback: Math.round((s.precioNeto || 0) * 1.19),
          tipoPrestacionFallback: 'Imágenes',
        };
      }),
      notas: `Generado automáticamente desde orden ${orderNumber || ''}`,
      updateOrderCollection: 'imagingOrders',
    });
  };

  const handleSubmit = async () => {
    if (!selectedPatient) return setSnackbar({ open: true, message: 'Selecciona un paciente.', severity: 'warning' });
    if (!selectedVet) return setSnackbar({ open: true, message: 'Selecciona un médico veterinario.', severity: 'warning' });
    if (selectedStudies.length === 0) return setSnackbar({ open: true, message: 'Selecciona al menos un estudio.', severity: 'warning' });
    if (!clinicalIndication) return setSnackbar({ open: true, message: 'Ingresa la indicación clínica.', severity: 'warning' });

    setSubmitting(true);
    try {
      const orderNumber = await getNextOrderNumber();
      const vetName = `Dr/a. ${selectedVet.nombre} ${selectedVet.apellidos || ''}`.trim();
      const { subtotal, iva, total } = calculateTotal();

      const orderData = {
        orderNumber,
        branchId: activeBranchId || '',
        branchName: userData?.branchName || '—',
        clinicId: userData?.clinicId || 'default',
        patientId: selectedPatient.id,
        patientName: selectedPatient.name,
        patientSpecies: selectedPatient.species || '',
        patientSex: selectedPatient.genero || '',
        patientBirthdate: selectedPatient.birthDate || null,
        patientBreed: selectedPatient.breed || '',
        patientWeight: selectedPatient.weight || 0,
        patientAge: calcAge(selectedPatient.birthDate),
        ownerId: selectedPatient.ownerId || '',
        ownerName: selectedPatient.ownerName || '',
        ownerPhone: selectedPatient.ownerPhone || '',
        ownerEmail: selectedPatient.ownerEmail || '',
        vetId: selectedVet.id,
        vetName,
        radiologistId: null,
        radiologistName: null,
        technicianId: null,
        technicianName: null,
        requestedAt: serverTimestamp(),
        studiesRequested: selectedStudies,
        clinicalIndication,
        relevantHistory,
        provisionalDiagnosis,
        priority,
        orderType: 'inhouse',
        teleradiology: null,
        files: [],
        status: 'pending_imaging',
        statusHistory: [{
          status: 'pending_imaging',
          changedAt: new Date(),
          changedBy: currentUser?.uid,
          changedByName: userData?.name || userData?.email,
          notes: 'Orden creada',
        }],
        resultId: null,
        reportGeneratedAt: null,
        paymentStatus: 'no_pagado',
        paymentId: null,
        createdBy: currentUser?.uid || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        hospitalizationId: null,
        labOrderId: null,
        surgeryId: null,
        sourceModule: 'direct',
      };
      const orderRef = await addDoc(collection(db, 'imagingOrders'), orderData);

      await addDoc(collection(db, 'imagingOrders', orderRef.id, 'auditLog'), {
        action: 'order_created',
        details: `Orden ${orderNumber} creada. Estudios: ${selectedStudies.map((s) => s.examTypeName).join(', ')}`,
        performedBy: currentUser?.uid,
        performedByName: userData?.name || userData?.email,
        timestamp: serverTimestamp(),
      });

      setSnackbar({ open: true, message: `Orden ${orderNumber} creada exitosamente.`, severity: 'success' });
      let presupuesto = null;
      try { presupuesto = await crearPresupuestoDesdeOrden(orderNumber, orderRef.id, 'imagingOrder', selectedStudies); } catch (err) { console.error('Error creando presupuesto:', err); }
      await printOrderPDF({ ...orderData, id: orderRef.id }, activeBranch);
      if (isGestion) {
        setCreatedOrder({ id: orderRef.id, orderNumber, presupuestoId: presupuesto?.id || null, studiesRequested: selectedStudies });
        setPaymentOpen(true);
      } else {
        setTimeout(() => navigate(homeUrl), 1200);
      }
    } catch (err) {
      console.error('Error creating order:', err);
      setSnackbar({ open: true, message: `Error al crear la orden: ${err.message}`, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePay = async ({ paymentMethod, boletaNumber, voucherNumber, last4Digits, paymentNotes }) => {
    const venta = await registrarVentaOrden({
      presupuestoId: createdOrder.presupuestoId || null,
      orderId: createdOrder.id,
      orderCollection: 'imagingOrders',
      orderType: 'imagingOrder',
      orderNumber: createdOrder.orderNumber,
      patientId: selectedPatient?.id || '',
      patientName: selectedPatient?.name || '',
      patientSpecies: selectedPatient?.species || '',
      ownerId: selectedPatient?.ownerId || '',
      ownerName: selectedPatient?.ownerName || '',
      fallbackItems: (createdOrder.studiesRequested || []).map((s) => ({
        nombre: s.examTypeName, cantidad: 1,
        precioUnitario: Math.round((s.precioNeto || 0) * 1.19),
        tipoPrestacion: 'Imágenes', unidadNegocio: 'Imágenes',
      })),
      pago: { paymentMethod, boletaNumber, voucherNumber, last4Digits, paymentNotes },
      branchId: activeBranchId || userData?.branchId || '',
      user: { uid: currentUser?.uid, name: userData?.name, email: currentUser?.email },
    });
    await addDoc(collection(db, 'imagingOrders', createdOrder.id, 'auditLog'), {
      action: 'payment_registered',
      details: `Pago registrado. Total: $${venta.total}. Método: ${paymentMethod}. Boleta: ${boletaNumber || 'N/A'}`,
      performedBy: currentUser?.uid,
      performedByName: userData?.name || userData?.email,
      timestamp: serverTimestamp(),
    });
    setPaymentOpen(false);
    navigate(homeUrl);
  };

  if (loadingData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const { subtotal, iva, total } = calculateTotal();

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => urlPatientId ? navigate(`/patients/${urlPatientId}/record`) : navigate(homeUrl)}
          sx={{ color: IMG_PRIMARY, fontWeight: 600 }}>Volver</Button>
        <BrokenImageIcon sx={{ color: IMG_PRIMARY, fontSize: 32 }} />
        <Typography variant="h5" fontWeight={700} color={IMG_PRIMARY}>Nueva Orden de Imagen</Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left: Patient + Clinical info */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} color={IMG_PRIMARY}>
                Paciente y Solicitante
              </Typography>
              {selectedPatient && (
                <IconButton size="small" onClick={() => setSelectedPatient(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {!selectedPatient ? (
              <>
                {loadingPatient ? (
                  <CircularProgress size={24} />
                ) : (
                  <PatientSearch
                    onPatientSelect={setSelectedPatient}
                    selectedPatient={selectedPatient}
                    onNewPatient={() => setNewPatientOpen(true)}
                    showFilters={true}
                    label="Buscar paciente"
                  />
                )}
              </>
            ) : (
              <Box sx={{ p: 2, mb: 2, bgcolor: '#F0FDF4', borderRadius: 2, border: '1px solid #BBF7D0' }}>
                <Typography variant="body2" fontWeight={700}>{selectedPatient.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedPatient.species} · {selectedPatient.breed} · {selectedPatient.genero}
                  {selectedPatient.weight ? ` · ${selectedPatient.weight} kg` : ''}
                </Typography>
                {selectedPatient.ownerName && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    Tutor: {selectedPatient.ownerName}
                    {selectedPatient.ownerRut && ` (${selectedPatient.ownerRut})`}
                  </Typography>
                )}
              </Box>
            )}

            <Autocomplete
              options={professionals}
              getOptionLabel={(p) => `Dr/a. ${p.nombre} ${p.apellidos || ''}`}
              value={selectedVet}
              disabled
              disableClearable
              renderInput={(params) => (
                <TextField {...params} label="Médico veterinario solicitante *" size="small" sx={{ mb: 2 }} InputLabelProps={{ shrink: true }} />
              )}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Indicación clínica *"
              value={clinicalIndication}
              onChange={(e) => setClinicalIndication(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Historia relevante"
              value={relevantHistory}
              onChange={(e) => setRelevantHistory(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <Autocomplete
              fullWidth
              options={diagnoses}
              getOptionLabel={(d) => d.diagnosis || ''}
              value={diagnoses.find(d => d.diagnosis === provisionalDiagnosis) || null}
              onChange={(_, v) => setProvisionalDiagnosis(v?.diagnosis || '')}
              renderInput={(params) => (
                <TextField {...params} label="Diagnóstico presuntivo" size="small" InputLabelProps={{ shrink: true }} />
              )}
              freeSolo
            />

            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" fontWeight={700} display="block" mb={1}>Prioridad</Typography>
              <ToggleButtonGroup
                value={priority}
                exclusive
                onChange={(e, v) => v && setPriority(v)}
                size="small"
                fullWidth
              >
                {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                  <ToggleButton key={key} value={key} sx={{ flex: 1 }}>
                    {cfg.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Paper>
        </Grid>

        {/* Right: Studies */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Typography variant="subtitle1" fontWeight={700} color={IMG_PRIMARY} mb={2}>
              Estudios a Solicitar
            </Typography>

            {displayedCategories.map(({ category, exams }) => (
              <Box key={category} sx={{ mb: 2 }}>
                <Typography variant="caption" fontWeight={700} color={IMG_TEAL}
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 0.5 }}>
                  {category}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                  {exams.map((e) => {
                    const selected = selectedStudies.some((s) => s.examTypeId === e.id);
                    return (
                      <Chip
                        key={e.id}
                        label={`${e.name} (${e.modality})`}
                        onClick={() => handleToggleStudy(e)}
                        variant={selected ? 'filled' : 'outlined'}
                        sx={{
                          bgcolor: selected ? IMG_PRIMARY : 'transparent',
                          color: selected ? '#fff' : IMG_PRIMARY,
                          borderColor: IMG_PRIMARY,
                          fontWeight: 600,
                          fontSize: '0.78rem',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: selected ? '#0F2A45' : '#EFF6FF' },
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            ))}

            {examTypes.length === 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                No hay tipos de examen configurados.{' '}
                <Button size="small" onClick={() => navigate('/imaging/catalog')}>Configurar catálogo</Button>
              </Alert>
            )}

            {selectedStudies.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No hay estudios seleccionados
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {selectedStudies.map((study, idx) => (
                  <Box key={idx} sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={study.modality} size="small" sx={{ fontSize: '0.65rem', bgcolor: MODALITY_CONFIG[study.modality]?.bg, color: MODALITY_CONFIG[study.modality]?.color }} />
                        <Typography variant="body2" fontWeight={600}>{study.examTypeName}</Typography>
                      </Box>
                      <IconButton size="small" onClick={() => handleRemoveStudy(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Grid container spacing={1} size="small">
                      <Grid size={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Región"
                          value={study.bodyRegion}
                          onChange={(e) => handleUpdateStudy(idx, 'bodyRegion', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid size={6}>
                        <TextField
                          fullWidth
                          size="small"
                          select
                          label="Lado"
                          value={study.side || ''}
                          onChange={(e) => handleUpdateStudy(idx, 'side', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        >
                          <MenuItem value="">N/A</MenuItem>
                          <MenuItem value="Derecho">Derecho</MenuItem>
                          <MenuItem value="Izquierdo">Izquierdo</MenuItem>
                          <MenuItem value="Bilateral">Bilateral</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid size={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Vistas"
                          value={study.views?.join(', ') || ''}
                          onChange={(e) => handleUpdateStudy(idx, 'views', e.target.value.split(',').map(v => v.trim()))}
                          placeholder="AP, Lateral"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid size={6}>
                        <TextField
                          fullWidth
                          size="small"
                          select
                          label="Contraste"
                          value={study.contrastRequired ? (study.contrastType || 'Yodado IV') : 'No'}
                          onChange={(e) => {
                            const val = e.target.value;
                            handleUpdateStudy(idx, 'contrastRequired', val !== 'No');
                            handleUpdateStudy(idx, 'contrastType', val !== 'No' ? val : null);
                          }}
                          InputLabelProps={{ shrink: true }}
                        >
                          <MenuItem value="No">No</MenuItem>
                          <MenuItem value="Yodado IV">Yodado IV</MenuItem>
                          <MenuItem value="Bario oral">Bario oral</MenuItem>
                        </TextField>
                      </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip
                        label={study.sedationRequired ? 'Requiere sedación' : 'Sin sedación'}
                        size="small"
                        sx={{ fontSize: '0.65rem', bgcolor: study.sedationRequired ? '#FEF3C7' : '#F1F5F9', color: study.sedationRequired ? '#92400E' : '#64748B' }}
                        onClick={() => handleUpdateStudy(idx, 'sedationRequired', !study.sedationRequired)}
                        clickable
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            <Button
              fullWidth
              variant="contained"
              startIcon={submitting ? <CircularProgress size={20} /> : <AddIcon />}
              onClick={handleSubmit}
              disabled={submitting || selectedStudies.length === 0}
              sx={{ mt: 3, bgcolor: IMG_TEAL, '&:hover': { bgcolor: '#229984' }, fontWeight: 600 }}
            >
              {submitting ? 'Creando...' : 'Solicitar Estudios'}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <PaymentDialog open={paymentOpen} onClose={() => { setPaymentOpen(false); setCreatedOrder(null); }} onPay={handlePay} />
      <NewPatientDialog open={newPatientOpen} onClose={() => setNewPatientOpen(false)} onPatientCreated={(patient) => setSelectedPatient(patient)} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
